import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { SHSidebar } from '@/sidebar/SHSidebar';
import { SHStudentViewSidebar } from '@/sidebar/SHStudentViewSidebar';
import { StudentSidebar } from '@/sidebar/StudentSidebar';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export const Route = createFileRoute('/_app/_users/members/$memberId')({
  component: MemberProfileLayout,
});

function MemberProfileLayout() {
  const matches = useMatches();
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const { isStudent, isStudyHelper, isViewingOwnProfile } = useCurrentUser();

  // /messages route screens follow this URL path, but don't share the same layout as the rest of the members/$memberId pages
  const isMessagesRoute = matches.some((m) =>
    (m.routeId as string).includes('/messages'),
  );

  if (isMessagesRoute) return <Outlet />;

  const isOwnProfile = isViewingOwnProfile(memberId);

  // Show StudentSidebar if a student is viewing own profile
  const shouldShowStudentSidebar = isOwnProfile && isStudent;

  // Coordinators can also view student profiles of students in their congregation group
  // When they do this for students who aren't their own, the only nav is Profile, Timeline, Course Study, and Student Notes. The Message button will not be visible.
  // TODO: Implement coordinator student profile viewing logic

  // Show SHStudentViewSidebar if a study helper is viewing a student
  const shouldShowSHStudentViewSidebar = isStudyHelper && !isOwnProfile;

  return (
    <Container
      style={
        isStudyHelper && !isOwnProfile ? { paddingTop: '20px' } : undefined
      }
    >
      {isStudyHelper && !isOwnProfile && (
        <AlertBox variant="warning">
          {t('profile.you-are-currently-viewing')}
        </AlertBox>
      )}
      <ContentWrapper>
        {shouldShowStudentSidebar ? (
          <StudentSidebar />
        ) : shouldShowSHStudentViewSidebar ? (
          <SHStudentViewSidebar userId={memberId} />
        ) : (
          <SHSidebar userId={memberId} />
        )}

        <PageContent>
          <Outlet />
        </PageContent>
      </ContentWrapper>
    </Container>
  );
}

const Container = styled.div`
  background-color: var(--bg-default);
  display: flex;
  flex-direction: column;
  min-height: 70vh;
`;

const ContentWrapper = styled.div`
  display: flex;

  @media (max-width: 1099px) {
    flex-direction: column;
  }
`;

const PageContent = styled.div`
  box-sizing: border-box;
  flex: 1;
  max-width: calc(100% - 296px);
  overflow: visible;
  padding: 14px 0 50px 50px;

  @media (max-width: 1099px) {
    max-width: 100%;
    padding: 14px 16px 50px;
  }
`;
